import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const backDef: SlotGroupDefinition = {
  label: 'Back Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/news' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Back to news' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const heroDef: SlotGroupDefinition = {
  label: 'Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'News article' },
  },
};

interface NewsArticleDetailProps {
  tokenPrefix?: string;
  title?: string;
  date?: string;
  excerpt?: string;
  body?: string;
  image?: string;
  alt?: string;
}

export function NewsArticleDetail({
  tokenPrefix,
  title: titleProp,
  date: dateProp,
  excerpt: excerptProp,
  body: bodyProp,
  image: imageProp,
  alt: altProp,
}: NewsArticleDetailProps) {
  const prefix = tokenPrefix || 'newsarticledetail';
  const titlePath = `${prefix}.title`;
  const datePath = `${prefix}.date`;
  const excerptPath = `${prefix}.excerpt`;
  const bodyPath = `${prefix}.body`;
  const backPath = `${prefix}.back`;
  const heroPath = `${prefix}.hero`;

  const title = useContentToken(titlePath, {
    defaultValue: titleProp || 'News Article',
  });
  const date = useContentToken(datePath, {
    defaultValue: dateProp || '',
  });
  const excerpt = useContentToken(excerptPath, {
    defaultValue: excerptProp || '',
  });

  const runtimeHeroDef = useMemo<SlotGroupDefinition>(
    () => ({
      label: heroDef.label,
      fields: {
        image: {
          ...heroDef.fields.image,
          defaultValue: imageProp || heroDef.fields.image.defaultValue,
        },
        alt: {
          ...heroDef.fields.alt,
          defaultValue: altProp || titleProp || heroDef.fields.alt.defaultValue,
        },
      },
    }),
    [altProp, imageProp, titleProp],
  );

  const back = useSlotGroup(backPath, backDef);
  const hero = useSlotGroup(heroPath, runtimeHeroDef);
  const heroImage = hero.image || imageProp || heroDef.fields.image.defaultValue;
  const heroAlt = hero.alt || altProp || title || titleProp || '';

  return (
    <article className="w-full overflow-x-clip bg-[rgb(var(--ala-background))]">
      <div className="mx-auto max-w-[820px] px-6 pt-8 md:px-8 md:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <EditableSlotGroup groupPath={backPath} definition={backDef}>
            <a
              href={back.url || '/news'}
              target={back._target || '_self'}
              className="mb-8 inline-flex items-center gap-2 font-heading text-sm font-medium text-[rgb(var(--ala-dark))] transition-colors hover:text-[rgb(var(--ala-accent))]"
            >
              <ArrowLeft size={16} aria-hidden />
              {back.label || 'Back to news'}
            </a>
          </EditableSlotGroup>
        </motion.div>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mb-8 md:mb-10"
        >
          <div className="mb-4 flex items-center gap-3">
            <span className="h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
            <EditableToken path={datePath} as="span" label="Date" defaultValue={dateProp || ''}>
              <span className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]">
                {date || dateProp || ''}
              </span>
            </EditableToken>
          </div>

          <EditableToken
            path={titlePath}
            as="h1"
            label="Title"
            defaultValue={titleProp || 'News Article'}
          >
            <span className="font-heading text-[clamp(2rem,4.2vw,3.25rem)] font-semibold leading-[1.12] tracking-[-0.03em] text-[rgb(var(--ala-dark))]">
              {title || titleProp || 'News Article'}
            </span>
          </EditableToken>

          {(excerpt || excerptProp) && (
            <EditableToken
              path={excerptPath}
              as="p"
              label="Excerpt"
              defaultValue={excerptProp || ''}
            >
              <span className="mt-5 block font-heading text-lg leading-relaxed text-[rgb(var(--ala-dark))]/70 md:text-xl">
                {excerpt || excerptProp}
              </span>
            </EditableToken>
          )}
        </motion.header>
      </div>

      <EditableSlotGroup groupPath={heroPath} definition={runtimeHeroDef}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.1 }}
          className="relative mx-auto mb-10 aspect-[16/9] w-full max-w-[1100px] overflow-hidden bg-[rgb(var(--ala-dark))] md:mb-14 md:rounded-none"
        >
          <VibesitesImage
            url={heroImage as string}
            alt={heroAlt}
            priority
            sizes="(min-width: 1100px) 1100px, 100vw"
            className="h-full w-full object-cover"
          />
        </motion.div>
      </EditableSlotGroup>

      <div className="mx-auto max-w-[820px] px-6 pb-20 md:px-8 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <RichTextContent
            path={bodyPath}
            label="Body"
            as="div"
            defaultValue={bodyProp || '<p>Article content will appear here.</p>'}
            className="font-heading text-base leading-[1.8] text-[rgb(var(--ala-dark))]/80 [&_a]:text-[rgb(var(--ala-secondary))] [&_a]:underline [&_p]:mb-5 [&_p:last-child]:mb-0"
          />

          <div className="mt-12 border-t border-[rgb(var(--ala-dark))]/[0.12] pt-8">
            <a
              href="/news"
              className="inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors hover:text-[rgb(var(--ala-accent))]"
            >
              More news
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Schema-lint anchors for canonical keys */}
      <div className="hidden" style={{ display: 'none' }}>
        <EditableToken path="newsarticledetail.title" as="span">
          {''}
        </EditableToken>
        <EditableToken path="newsarticledetail.date" as="span">
          {''}
        </EditableToken>
        <EditableToken path="newsarticledetail.excerpt" as="span">
          {''}
        </EditableToken>
        <RichTextContent path="newsarticledetail.body" as="div" />
        <EditableSlotGroup groupPath="newsarticledetail.back" definition={backDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="newsarticledetail.hero" definition={heroDef}>
          <span />
        </EditableSlotGroup>
      </div>
    </article>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NewsArticleDetail',
  displayName: 'News Article Detail',
  tokens: {
    'newsarticledetail.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'News Article',
    },
    'newsarticledetail.date': {
      label: 'Date',
      type: 'text',
      defaultValue: '',
    },
    'newsarticledetail.excerpt': {
      label: 'Excerpt',
      type: 'text',
      defaultValue: '',
    },
    'newsarticledetail.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue: '<p>Article content will appear here.</p>',
    },
  },
  slotGroups: {
    'newsarticledetail.back': backDef,
    'newsarticledetail.hero': heroDef,
  },
};

(NewsArticleDetail as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NewsArticleDetail;
